import { useState, useEffect } from 'react';
import api, { handleApiError, queryParams } from '../services/api';
import { formatDate, formatDateTime } from '../utils/formatters';
import toast from 'react-hot-toast';
import {
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  Lock,
  Unlock,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

function PeriodRow({ period, onClose, onReopen }) {
  const isOpen = period.status === 'open' || period.is_active;

  return (
    <tr>
      <td>
        <span className="font-medium text-gray-900 dark:text-white">{period.year}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{period.month_name || period.month || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{period.period_name || period.period || `Periode ${period.month} ${period.year}`}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(period.start_date)}</span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{formatDate(period.end_date)}</span>
      </td>
      <td>
        <span className={`badge ${isOpen ? 'badge-success' : 'badge-danger'}`}>
          {isOpen ? 'Terbuka' : 'Ditutup'}
        </span>
      </td>
      <td>
        <span className="text-sm text-gray-700 dark:text-gray-200">{period.closed_by_user?.name || period.closed_by_name || period.closed_by || '-'}</span>
      </td>
      <td>
        <span className="text-sm text-gray-500 dark:text-gray-400">{period.closed_at ? formatDateTime(period.closed_at) : '-'}</span>
      </td>
      <td>
        <div className="flex items-center gap-2">
          {isOpen ? (
            <button onClick={() => onClose(period)} className="p-1.5 text-danger-600 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded" title="Tutup Periode">
              <Lock className="h-4 w-4" />
            </button>
          ) : (
            <button onClick={() => onReopen(period)} className="p-1.5 text-warning-600 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded" title="Buka Ulang Periode">
              <Unlock className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AccountingPeriodsPage() {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ year: '', status: '' });

  // Confirmation modals
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPeriods();
  }, [pagination.page]);

  const fetchPeriods = async () => {
    setLoading(true);
    try {
      const params = queryParams({ page: pagination.page, limit: pagination.limit, ...filters });
      const res = await api.get('/accounting-periods', { params });
      if (res.data.success) {
        const data = res.data.data;
        setPeriods(data?.periods || data?.data || []);
        setPagination((prev) => ({
          ...prev,
          total: data?.total || data?.pagination?.total || 0,
          totalPages: data?.totalPages || data?.pagination?.totalPages || Math.ceil((data?.total || 0) / prev.limit),
        }));
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchPeriods();
  };

  const resetFilters = () => {
    setFilters({ year: '', status: '' });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleClosePeriod = async () => {
    if (!selectedPeriod) return;
    setSaving(true);
    try {
      const res = await api.post(`/accounting-periods/${selectedPeriod.id}/close`);
      if (res.data.success) {
        toast.success('Periode berhasil ditutup');
        setShowCloseModal(false);
        setSelectedPeriod(null);
        fetchPeriods();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const handleReopenPeriod = async () => {
    if (!selectedPeriod) return;
    setSaving(true);
    try {
      const res = await api.post(`/accounting-periods/${selectedPeriod.id}/reopen`);
      if (res.data.success) {
        toast.success('Periode berhasil dibuka kembali');
        setShowReopenModal(false);
        setSelectedPeriod(null);
        fetchPeriods();
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setSaving(false);
    }
  };

  const openCloseModal = (period) => {
    setSelectedPeriod(period);
    setShowCloseModal(true);
  };

  const openReopenModal = (period) => {
    setSelectedPeriod(period);
    setShowReopenModal(true);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Periode Akuntansi</h1>
          <p className="page-subtitle">Kelola periode akuntansi dan siklus penutupan</p>
        </div>
      </div>

      {/* Info Card */}
      <div className="card bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800">
        <div className="card-body flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-primary-800 dark:text-primary-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-primary-900 dark:text-primary-100">Informasi Periode Akuntansi</p>
            <p className="text-xs text-primary-900 dark:text-primary-300 mt-1">
              Menutup periode akan mengunci semua jurnal pada periode tersebut. Periode yang sudah ditutup tidak dapat diubah kecuali dibuka kembali oleh pengguna yang berwenang.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <select className="input" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}>
                <option value="">Semua Tahun</option>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div>
              <select className="input" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
                <option value="">Semua Status</option>
                <option value="open">Terbuka</option>
                <option value="closed">Ditutup</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={handleFilter} className="btn-primary btn-sm flex-1">
                <Search className="h-4 w-4 mr-1" /> Filter
              </button>
              <button onClick={resetFilters} className="btn-secondary btn-sm">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            <div className="space-y-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex gap-4 items-center">
                  <div className="skeleton h-4 w-16" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-28" />
                  <div className="skeleton h-4 w-20" />
                  <div className="skeleton h-4 w-24" />
                  <div className="skeleton h-4 w-32" />
                  <div className="skeleton h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        ) : periods.length === 0 ? (
          <div className="card-body py-16 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Calendar className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">Tidak Ada Periode</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Belum ada periode akuntansi yang terdaftar.</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Tahun</th>
                    <th>Bulan</th>
                    <th>Periode</th>
                    <th>Tanggal Mulai</th>
                    <th>Tanggal Akhir</th>
                    <th>Status</th>
                    <th>Ditutup Oleh</th>
                    <th>Waktu Tutup</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p) => (
                    <PeriodRow
                      key={p.id}
                      period={p}
                      onClose={openCloseModal}
                      onReopen={openReopenModal}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Menampilkan {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} dari {pagination.total} data
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page <= 1}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-gray-700 dark:text-gray-300 px-2">
                    Halaman {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page >= pagination.totalPages}
                    className="btn btn-sm btn-outline"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Close Period Confirmation Modal */}
      {showCloseModal && selectedPeriod && (
        <div className="modal-overlay" onClick={() => setShowCloseModal(false)}>
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Konfirmasi Tutup Periode</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-warning-100 dark:bg-warning-900/30">
                  <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Anda akan menutup periode <strong>{selectedPeriod.period_name || selectedPeriod.period || `Periode ${selectedPeriod.month} ${selectedPeriod.year}`}</strong>.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Setelah ditutup, jurnal baru tidak dapat dibuat pada periode ini. Apakah Anda yakin?
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Periode</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedPeriod.month_name || selectedPeriod.month} {selectedPeriod.year}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Rentang Tanggal</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatDate(selectedPeriod.start_date)} - {formatDate(selectedPeriod.end_date)}
                  </span>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowCloseModal(false)} className="btn-secondary flex-1">Batal</button>
                <button onClick={handleClosePeriod} disabled={saving} className="btn-danger flex-1">
                  {saving ? 'Memproses...' : 'Ya, Tutup Periode'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reopen Period Confirmation Modal */}
      {showReopenModal && selectedPeriod && (
        <div className="modal-overlay" onClick={() => setShowReopenModal(false)}>
          <div className="bg-white/95 dark:bg-surface-900/95 rounded-3xl border border-white/80 dark:border-white/10 shadow-modal backdrop-blur-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-surface-100 dark:border-surface-800">
              <h3 className="text-base font-semibold text-surface-900 dark:text-white">Konfirmasi Buka Ulang Periode</h3>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-warning-100 dark:bg-warning-900/30">
                  <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-200">
                    Anda akan membuka kembali periode <strong>{selectedPeriod.period_name || selectedPeriod.period || `Periode ${selectedPeriod.month} ${selectedPeriod.year}`}</strong>.
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Periode yang dibuka kembali akan memungkinkan pembuatan dan perubahan jurnal. Pastikan Anda memiliki otorisasi yang tepat.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Periode</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedPeriod.month_name || selectedPeriod.month} {selectedPeriod.year}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Ditutup Oleh</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedPeriod.closed_by_user?.name || selectedPeriod.closed_by_name || selectedPeriod.closed_by || '-'}
                  </span>
                </div>
                {selectedPeriod.closed_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">Ditutup Pada</span>
                    <span className="font-medium text-gray-900 dark:text-white">{formatDateTime(selectedPeriod.closed_at)}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowReopenModal(false)} className="btn-secondary flex-1">Batal</button>
                <button onClick={handleReopenPeriod} disabled={saving} className="btn-warning flex-1">
                  {saving ? 'Memproses...' : 'Ya, Buka Ulang'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
